import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Platform } from 'react-native';
import type {
  DramaAnalysisResponse,
  ResultsPrimaryActionTreatment,
  ShareTreatment,
} from '@dramadetector/shared';

import { captureEvent } from '../lib/analytics';

type ResultsFunnelSurface = 'home_inline' | 'results_page' | 'shared_link';
type ResultsFunnelRenderVariant = 'preview' | 'full' | 'generative';

interface UseResultsFunnelAnalyticsOptions {
  analysis: DramaAnalysisResponse | null | undefined;
  tier: string;
  surface: ResultsFunnelSurface;
  renderVariant: ResultsFunnelRenderVariant;
  showLikelyImpact: boolean;
  showPrimaryRewriteCard: boolean;
  primaryActionTreatment: ResultsPrimaryActionTreatment;
  shareTreatment: ShareTreatment;
}

function getPrimaryRewrite(analysis: DramaAnalysisResponse | null | undefined) {
  return analysis?.result?.rewrites?.[0] ?? analysis?.rewrites?.[0] ?? null;
}

export function useResultsFunnelAnalytics({
  analysis,
  tier,
  surface,
  renderVariant,
  showLikelyImpact,
  showPrimaryRewriteCard,
  primaryActionTreatment,
  shareTreatment,
}: UseResultsFunnelAnalyticsOptions) {
  const trackedAnalysisIdRef = useRef<string | null>(null);
  const platform = Platform.OS as 'ios' | 'android' | 'web';
  const primaryRewrite = useMemo(() => getPrimaryRewrite(analysis), [analysis]);
  const score = analysis?.result?.score ?? analysis?.score ?? null;
  const category = analysis?.result?.category ?? analysis?.category ?? null;

  // eslint-disable-next-line local-rules/no-race-conditions -- one-shot per-analysis exposure tracking guarded by trackedAnalysisIdRef; this effect only emits analytics and does not coordinate async state.
  // eslint-disable-next-line local-rules/no-race-conditions -- one-shot per-analysis telemetry gated by trackedAnalysisIdRef; emits analytics only and does not coordinate shared mutable state
  useEffect(() => {
    if (!analysis?.id || surface === 'shared_link') return;
    if (trackedAnalysisIdRef.current === analysis.id) return;

    if (showLikelyImpact) {
      captureEvent('Results_LikelyImpact_Shown', {
        analysis_id: analysis.id,
        surface,
        render_variant: renderVariant,
        tier,
        score,
        category,
        share_treatment: shareTreatment,
        primary_action_treatment: primaryActionTreatment,
        platform,
      });
    }

    if (showPrimaryRewriteCard && primaryRewrite) {
      captureEvent('Results_PrimaryRewrite_Shown', {
        analysis_id: analysis.id,
        surface,
        render_variant: renderVariant,
        tier,
        score,
        category,
        has_reasoning: Boolean(primaryRewrite.reasoning),
        share_treatment: shareTreatment,
        primary_action_treatment: primaryActionTreatment,
        platform,
      });
    }

    trackedAnalysisIdRef.current = analysis.id;
  }, [
    analysis,
    category,
    platform,
    primaryActionTreatment,
    primaryRewrite,
    renderVariant,
    score,
    shareTreatment,
    showLikelyImpact,
    showPrimaryRewriteCard,
    surface,
    tier,
  ]);

  const trackPrimaryRewriteCopied = useCallback(() => {
    if (!analysis?.id || surface === 'shared_link') return;

    captureEvent('Results_PrimaryRewrite_Copied', {
      analysis_id: analysis.id,
      surface,
      render_variant: renderVariant,
      tier,
      score,
      category,
      share_treatment: shareTreatment,
      primary_action_treatment: primaryActionTreatment,
      platform,
    });
  }, [
    analysis,
    category,
    platform,
    primaryActionTreatment,
    renderVariant,
    score,
    shareTreatment,
    surface,
    tier,
  ]);

  return {
    trackPrimaryRewriteCopied,
  };
}

export type { ResultsFunnelSurface, ResultsFunnelRenderVariant };
